// Paynecta Payment Link Service
// Handles Paynecta payment integration via payment links

class PaynectaService {
    constructor() {
        this.baseUrl = 'https://paynecta.co.ke';
        this.merchantSlug = 'think2';
    }

    // Format phone number to required format (254XXXXXXXXX)
    formatPhoneNumber(phone) {
        // Remove any spaces or special characters
        let cleanPhone = phone.replace(/\D/g, '');
        
        // Convert to international format
        if (cleanPhone.startsWith('0')) {
            cleanPhone = '254' + cleanPhone.slice(1);
        } else if (cleanPhone.startsWith('254')) {
            // Already in correct format
        } else if (cleanPhone.startsWith('7') || cleanPhone.startsWith('1')) {
            cleanPhone = '254' + cleanPhone;
        }
        
        return cleanPhone;
    }

    // Validate phone number format (Safaricom only)
    validatePhoneNumber(phone) {
        const phoneStr = String(phone).trim();
        const formatted = this.formatPhoneNumber(phoneStr);
        // Valid: 254700000000 to 254799999999 (07XX) or 254100000000 to 254199999999 (01XX)
        const regex = /^254[17]\d{8}$/;
        return regex.test(formatted);
    }

    // Validate amount (1 to 250,000 KES)
    validateAmount(amount) {
        const amt = Number(amount);
        return amt >= 1 && amt <= 250000;
    }

    // Generate unique payment reference
    generatePaymentReference() {
        return `TT-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    }

    // Generate payment link URL
    generatePaymentLink(amount, phone, reference = null) {
        const formattedPhone = this.formatPhoneNumber(phone);
        let url = `${this.baseUrl}/pay/${this.merchantSlug}?amount=${amount}&phone=${formattedPhone}`;
        
        if (reference) {
            url += `&reference=${encodeURIComponent(reference)}`;
        }
        
        return url;
    }

    // Create a pending payment record and return payment link
    async createPaymentLink(phoneNumber, amount, orderData) {
        try {
            // Validate inputs
            if (!this.validatePhoneNumber(phoneNumber)) {
                throw new Error('Invalid Safaricom phone number. Use 07XX, 01XX, or 254XXX format');
            }

            if (!this.validateAmount(amount)) {
                throw new Error('Amount must be between 1 and 250,000 KES');
            }

            // Generate unique reference
            const reference = this.generatePaymentReference();
            
            // Store pending order data in sessionStorage with reference
            const pendingData = {
                reference,
                orderData,
                amount,
                phone: this.formatPhoneNumber(phoneNumber),
                timestamp: Date.now(),
                status: 'pending'
            };
            
            sessionStorage.setItem(`payment_${reference}`, JSON.stringify(pendingData));
            
            // Generate payment link
            const paymentUrl = this.generatePaymentLink(amount, phoneNumber, reference);
            
            console.log('Payment link created:', {
                reference,
                amount,
                phone: this.formatPhoneNumber(phoneNumber)
            });
            
            return {
                success: true,
                paymentUrl,
                reference,
                message: 'Payment link generated successfully'
            };
        } catch (error) {
            console.error('Payment Link Error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Initiate STK push via backend API
    async initiateSTKPush(phoneNumber, amount, orderId, reference = null) {
        try {
            if (!this.validatePhoneNumber(phoneNumber)) {
                throw new Error('Invalid Safaricom phone number. Use 07XX, 01XX, or 254XXX format');
            }

            if (!this.validateAmount(amount)) {
                throw new Error('Amount must be between 1 and 250,000 KES');
            }

            const paymentReference = reference || this.generatePaymentReference();
            
            // Determine the correct API endpoint
            // In production (Vercel), use relative path
            // In local dev, use the deployed production API
            const isProduction = window.location.hostname !== 'localhost';
            const apiEndpoint = isProduction
                ? '/api/paynecta/stkpush'
                : 'https://shanga-beauty-shop-v2.vercel.app/api/paynecta/stkpush';

            console.log('Initiating STK Push:', { apiEndpoint, isProduction });

            const response = await fetch(apiEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    phone: phoneNumber,
                    amount: Number(amount),
                    orderId,
                    reference: paymentReference
                })
            });

            const responseData = await response.json();

            if (!response.ok || !responseData.success) {
                throw new Error(responseData.error || 'Failed to initiate payment');
            }

            return {
                success: true,
                message: responseData.message,
                data: responseData.data,
                reference: paymentReference
            };
        } catch (error) {
            console.error('STK Push Error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Query payment status via backend API
    async queryPaymentStatus(reference) {
        try {
            if (!reference) {
                throw new Error('Payment reference is required');
            }

            // Determine the correct API endpoint
            const isProduction = window.location.hostname !== 'localhost';
            const apiEndpoint = isProduction
                ? '/api/paynecta/status'
                : 'https://shanga-beauty-shop-v2.vercel.app/api/paynecta/status';
            
            console.log('Querying payment status:', reference);
            
            const response = await fetch(
                `${apiEndpoint}?reference=${encodeURIComponent(reference)}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to query payment status');
            }

            const responseData = await response.json();
            console.log('Status Query Response:', responseData);
            
            return {
                success: true,
                data: responseData.data,
                status: responseData.data?.status
            };
        } catch (error) {
            console.error('Status Query Error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Poll payment status until completion (checks every 5 seconds, max 24 attempts = 2 minutes)
    async pollPaymentStatus(reference, onStatusUpdate = null, maxAttempts = 24, intervalSeconds = 5) {
        let attempts = 0;
        
        return new Promise((resolve) => {
            const pollInterval = setInterval(async () => {
                attempts++;
                console.log(`Polling attempt ${attempts}/${maxAttempts} for reference: ${reference}`);
                
                const result = await this.queryPaymentStatus(reference);
                
                // Notify caller of status update
                if (onStatusUpdate && result.success) {
                    onStatusUpdate(result.data);
                }
                
                if (result.success && result.data) {
                    const status = result.data.status;
                    
                    // Stop polling if payment is in final state
                    if (status === 'completed' || status === 'failed' || status === 'cancelled') {
                        clearInterval(pollInterval);
                        resolve(result);
                        return;
                    }
                }
                
                // Stop polling after max attempts
                if (attempts >= maxAttempts) {
                    clearInterval(pollInterval);
                    resolve({
                        success: false,
                        error: 'Payment verification timeout. Please check your M-Pesa messages.',
                        timeout: true
                    });
                }
            }, intervalSeconds * 1000);
        });
    }

    // Get pending payment data
    getPendingPayment(reference) {
        const data = sessionStorage.getItem(`payment_${reference}`);
        return data ? JSON.parse(data) : null;
    }

    // Clear pending payment data
    clearPendingPayment(reference) {
        sessionStorage.removeItem(`payment_${reference}`);
    }
}

const paynectaService = new PaynectaService();
export default paynectaService;
